import { create } from "zustand";
import { generateRooms, initialBlocks, initialSections } from "../data/objects.mock";
import type { Block, Building, Room, Section } from "../types/objects.types";

interface ObjectsState {
	buildings: Building[];
	blocks: Block[];
	sections: Section[];
	rooms: Room[];
	setBuildings: (buildings: Building[]) => void;
}

interface ObjectsActions {
	addBuilding: (
		building: Omit<Building, "blocksCount" | "soldCount" | "availableCount" | "totalCount">
	) => void;
	updateBuilding: (buildingId: string, updates: Partial<Building>) => void;
	addBlock: (block: Omit<Block, "id" | "roomTypesCount" | "priceRange" | "areaRange">) => void;
	addSection: (section: Omit<Section, "id" | "roomsCount">) => void;
	deleteSection: (sectionId: string) => boolean;
	addRoom: (room: Omit<Room, "id" | "status">) => void;
	updateRoom: (roomId: string, updates: Partial<Room>) => void;
}

type ObjectsStore = ObjectsState & ObjectsActions;

function getRoomStatusCountDiff(previousStatus: Room["status"], nextStatus?: Room["status"]) {
	if (!nextStatus || nextStatus === previousStatus) {
		return { soldDiff: 0, availDiff: 0 };
	}

	return {
		soldDiff: Number(nextStatus === "sotilgan") - Number(previousStatus === "sotilgan"),
		availDiff: Number(nextStatus === "sotuvda") - Number(previousStatus === "sotuvda"),
	};
}

function applyBuildingRoomStatusDiff(
	building: Building,
	buildingId: string,
	diff: { soldDiff: number; availDiff: number }
) {
	if (building.id !== buildingId) {
		return building;
	}

	return {
		...building,
		soldCount: Math.max(0, building.soldCount + diff.soldDiff),
		availableCount: Math.max(0, building.availableCount + diff.availDiff),
	};
}

export const useObjectsStore = create<ObjectsStore>((set, get) => ({
	buildings: [],
	blocks: initialBlocks,
	sections: initialSections,
	rooms: generateRooms(),

	setBuildings: (buildings) => {
		set({ buildings });
	},

	addBuilding: (newBuilding) => {
		const building: Building = {
			...newBuilding,
			blocksCount: 0,
			soldCount: 0,
			availableCount: 0,
			totalCount: 0,
		};
		set((state) => ({
			buildings: [...state.buildings, building],
		}));
	},

	updateBuilding: (buildingId, updates) => {
		set((state) => ({
			buildings: state.buildings.map((b) => (b.id === buildingId ? { ...b, ...updates } : b)),
		}));
	},

	addBlock: (newBlock) => {
		const blockId = `${newBlock.buildingId}-${newBlock.name.toLowerCase().replace(/\s+/g, "-")}`;
		const block: Block = {
			...newBlock,
			id: blockId,
			roomTypesCount: ["1-xonali: 0", "2-xonali: 0", "3-xonali: 0"],
			priceRange: "0 UZS",
			areaRange: "0 m²",
		};

		set((state) => {
			const updatedBuildings = state.buildings.map((b) => {
				if (b.id === newBlock.buildingId) {
					return { ...b, blocksCount: b.blocksCount + 1 };
				}
				return b;
			});

			return {
				blocks: [...state.blocks, block],
				buildings: updatedBuildings,
			};
		});
	},

	addSection: (newSection) => {
		const sectionId = `${newSection.blockId}-s${Math.floor(Math.random() * 10000)}`;
		const section: Section = {
			...newSection,
			id: sectionId,
			roomsCount: 0,
		};

		set((state) => {
			const updatedBlocks = state.blocks.map((b) => {
				if (b.id === newSection.blockId) {
					return { ...b, sectionsCount: b.sectionsCount + 1 };
				}
				return b;
			});

			return {
				sections: [...state.sections, section],
				blocks: updatedBlocks,
			};
		});
	},

	deleteSection: (sectionId) => {
		const { rooms, sections } = get();
		const sectionRooms = rooms.filter((r) => r.sectionId === sectionId);

		// Business rule: Cannot delete section with existing rooms
		if (sectionRooms.length > 0) {
			return false;
		}

		const targetSection = sections.find((s) => s.id === sectionId);
		if (!targetSection) {
			return false;
		}

		set((state) => {
			const updatedBlocks = state.blocks.map((b) => {
				if (b.id === targetSection.blockId) {
					return { ...b, sectionsCount: Math.max(0, b.sectionsCount - 1) };
				}
				return b;
			});

			return {
				sections: state.sections.filter((s) => s.id !== sectionId),
				blocks: updatedBlocks,
			};
		});

		return true;
	},

	addRoom: (newRoom) => {
		const roomId = `${newRoom.sectionId}-${newRoom.roomNumber}`;
		const room: Room = {
			...newRoom,
			id: roomId,
			status: "sotuvda",
		};

		set((state) => {
			// Update Section rooms count
			const updatedSections = state.sections.map((s) => {
				if (s.id === newRoom.sectionId) {
					return { ...s, roomsCount: s.roomsCount + 1 };
				}
				return s;
			});

			// Update Building stats
			const updatedBuildings = state.buildings.map((b) => {
				if (b.id === newRoom.buildingId) {
					return {
						...b,
						availableCount: b.availableCount + 1,
						totalCount: b.totalCount + 1,
					};
				}
				return b;
			});

			return {
				rooms: [...state.rooms, room],
				sections: updatedSections,
				buildings: updatedBuildings,
			};
		});
	},

	updateRoom: (roomId, updates) => {
		const previousRoom = get().rooms.find((r) => r.id === roomId);
		if (!previousRoom) {
			return;
		}

		set((state) => {
			const updatedRooms = state.rooms.map((r) => (r.id === roomId ? { ...r, ...updates } : r));
			const statusDiff = getRoomStatusCountDiff(previousRoom.status, updates.status);
			const updatedBuildings = state.buildings.map((building) =>
				applyBuildingRoomStatusDiff(building, previousRoom.buildingId, statusDiff)
			);

			return {
				rooms: updatedRooms,
				buildings: updatedBuildings,
			};
		});
	},
}));
